import type { RegisterButtonLocation } from "@repo/database";
import { useEffect, useRef, useState } from "react";
import PlayArrow from "@mui/icons-material/PlayArrow";

import type { Button, PartialButton } from "~/providers/button-provider";
import { useButton } from "~/hooks/use-button";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useMediaQuery } from "~/hooks/use-media-query";
import { cn } from "~/lib/utils";
import { useButtonManager } from "~/managers/use-button-manager";
import { useFunctionManager } from "~/managers/use-function-manager";
import { RegisterButton } from "../ui/register-button";
import { FunctionButton } from "./function-button";

export const FunctionButtons = ({
  location,
  orientation = "horizontal",
  length = 15,
}: {
  location: RegisterButtonLocation;
  orientation?: "horizontal" | "vertical";
  length?: number;
}) => {
  const { buttons } = useButton();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollAmount = 80;

  const [isScrollableUp, setIsScrollableUp] = useState(false);

  const [isScrollableDown, setIsScrollableDown] = useState(false);

  const [isScrollableLeft, setIsScrollableLeft] = useState(false);

  const [isScrollableRight, setIsScrollableRight] = useState(false);

  const { getFunction, getLabel, getDefaultColor, isDisabled, getContext } =
    useFunctionManager();

  const { openButtonDialog, setSelectedButton } = useButtonManager();

  const [ref] = useDragScroll();
  const [mobileRef] = useDragScroll();

  const isMobile = useMediaQuery(768);

  const filteredButtons = buttons.filter(
    (button) => button.location === location && button.mobile === isMobile,
  );

  const handleScroll = (dir: number) => {
    if (scrollRef.current) {
      if (orientation === "vertical") {
        scrollRef.current.scrollBy({
          top: dir === -1 ? -scrollAmount : scrollAmount,
        });
      } else {
        scrollRef.current.scrollBy({
          left: dir === -1 ? -scrollAmount : scrollAmount,
        });
      }

      checkScrollable();
    }
  };

  const checkScrollable = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollTop,
        scrollWidth,
        clientWidth,
        scrollHeight,
        clientHeight,
      } = scrollRef.current;
      setIsScrollableLeft(scrollLeft > 0);
      setIsScrollableUp(scrollTop > 0);
      setIsScrollableDown(scrollTop + clientHeight < scrollHeight);
      setIsScrollableRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const mobileButtons = filteredButtons.filter((b) => b.mobile);
  const desktopButtons = filteredButtons.filter((b) => !b.mobile);

  const firstButton = filteredButtons.find(
    (b) => b.position === 1 && b.mobile === false,
  );
  const lastButton = filteredButtons.find(
    (b) => b.position === 15 && b.mobile === false,
  );

  const handleContext = (index: number, button?: PartialButton) => {
    if (!button) {
      button = {
        location,
        position: index,
        mobile: isMobile,
      };
    }
    setSelectedButton(button);

    if (button.function) {
      const contextFunction = getContext(button.function);

      if (contextFunction) {
        void contextFunction();
        return;
      }
    }

    openButtonDialog();
  };

  const handleClick = (button?: Button) => {
    if (button) {
      if (isDisabled(button)) {
        return;
      }
      const buttonFunction = getFunction(button.function);
      if (buttonFunction) {
        buttonFunction();
      }
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      checkScrollable();

      scrollElement.addEventListener("scroll", checkScrollable);

      window.addEventListener("resize", checkScrollable);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
    };
  }, []);

  if (isMobile && mobileButtons.length > 0) {
    return (
      <div className="flex flex-col overflow-auto gap-0.5 border-t">
        {mobileButtons.find((b) => b.position > 3) && (
          <div
            ref={mobileRef}
            className="flex flex-row overflow-auto hide-scrollbar"
          >
            {Array.from({ length }, (_, i) => i + 4).map((position) => {
              const button = mobileButtons.find((b) => b.position === position);

              return (
                <FunctionButton
                  index={position}
                  disabled={isDisabled(button)}
                  onContext={() => handleContext(position, button)}
                  onClick={() => handleClick(button)}
                  label={button?.label ?? getLabel(button?.function)}
                  color={button?.color ?? getDefaultColor(button?.function)}
                  className="h-14 max-w-36"
                  key={position + 1}
                />
              );
            })}
          </div>
        )}

        <div className="flex flex-row">
          {[1, 2, 3].map((position) => {
            const button = mobileButtons.find((b) => b.position === position);

            return (
              <FunctionButton
                key={position}
                disabled={isDisabled(button)}
                index={position}
                className="min-h-14 w-full"
                onContext={() => handleContext(position, button)}
                onClick={() => handleClick(button)}
                label={button?.label ?? getLabel(button?.function)}
                color={button?.color ?? getDefaultColor(button?.function)}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (desktopButtons.length === 0) return null;

  return (
    <div
      className={cn(
        "flex overflow-auto border-t",
        orientation === "vertical" && "flex-col",
      )}
    >
      <FunctionButton
        index={1}
        onContext={() => handleContext(1, firstButton)}
        onClick={() => handleClick(firstButton)}
        label={getLabel(firstButton?.function)}
        color={firstButton?.color ?? getDefaultColor(firstButton?.function)}
        className="min-h-14 max-w-36"
      />
      <div
        className={cn(
          "flex flex-1 flex-row overflow-auto",
          orientation === "vertical" && "flex-col",
        )}
      >
        {desktopButtons.length > 1 && (
          <RegisterButton
            color="#000000"
            disabled={
              orientation === "vertical" ? !isScrollableUp : !isScrollableLeft
            }
            onClick={() => handleScroll(-1)}
            className="min-w-10 items-center justify-center rounded-none"
          >
            <PlayArrow
              className={cn(
                "rotate-180",
                orientation === "vertical" && "-rotate-90",
              )}
            />
          </RegisterButton>
        )}

        <div
          ref={(e) => {
            ref(e);
            scrollRef.current = e;
          }}
          className={cn(
            "hide-scrollbar flex flex-1 flex-row gap-0.5 overflow-auto",
            orientation === "vertical" && "flex-col",
          )}
        >
          {Array.from({ length: length - 2 }, (_, i) => i + 1).map(
            (position) => {
              const button = desktopButtons.find(
                (b) => b.position === position + 1,
              );

              return (
                <FunctionButton
                  index={position + 1}
                  className="min-h-14"
                  key={position + 1}
                  onContext={() => handleContext(position + 1, button)}
                  onClick={() => handleClick(button)}
                  label={button?.label ?? getLabel(button?.function)}
                  color={button?.color ?? getDefaultColor(button?.function)}
                />
              );
            },
          )}
        </div>

        {desktopButtons.length > 1 && (
          <RegisterButton
            color="#000000"
            disabled={
              orientation === "vertical"
                ? !isScrollableDown
                : !isScrollableRight
            }
            onClick={() => handleScroll(1)}
            className="min-w-10 items-center justify-center rounded-none"
          >
            <PlayArrow
              className={cn(orientation === "vertical" && "rotate-90")}
            />
          </RegisterButton>
        )}
      </div>

      <FunctionButton
        onContext={() => handleContext(15, lastButton)}
        onClick={() => handleClick(lastButton)}
        label={getLabel(lastButton?.function)}
        color={lastButton?.color ?? getDefaultColor(lastButton?.function)}
        index={15}
        className="min-h-14 max-w-36"
      />
    </div>
  );
};
